import { useState } from 'react';
import { Info } from 'lucide-react';
import { useClusterEvents, useClusterNamespaces } from './useClusterData';
import EventRow from './EventRow';

interface Props {
  siteId: string;
}

const TYPE_OPTIONS = ['All', 'Normal', 'Warning'] as const;

export default function EventsPanel({ siteId }: Props) {
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [namespace, setNamespace] = useState<string>('');

  const { data: namespaces } = useClusterNamespaces(siteId);
  const { data: events, isLoading } = useClusterEvents(siteId, {
    namespace: namespace || undefined,
    type: typeFilter !== 'All' ? typeFilter : undefined,
  });

  if (isLoading && !events) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {TYPE_OPTIONS.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                typeFilter === t
                  ? 'bg-gold/20 text-gold font-medium'
                  : 'text-silver-muted hover:text-silver-bright hover:bg-surface-elevated'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <select
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          className="bg-surface border border-surface-elevated rounded-lg px-3 py-1.5 text-xs text-silver-bright focus:border-gold focus:outline-none"
        >
          <option value="">All namespaces</option>
          {namespaces?.map((ns) => (
            <option key={ns} value={ns}>{ns}</option>
          ))}
        </select>
      </div>

      {/* Events list */}
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        {!events || events.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Info size={40} className="mx-auto text-silver-muted/30" />
            <p className="text-silver-muted">No events found</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-elevated">
            {events.map((event, i) => (
              <EventRow
                key={`${event.namespace}-${event.involvedObject.name}-${event.reason}-${i}`}
                event={event}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
